import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatCentralTime } from "../../../common";

const SortableTable = ({
  headers,
  data,
  default_sort_column = null,
  default_sort_direction = "asc",
  extra_controls = null,
  onRowClick = null,
  isRowClickable = (row) => true,
  mobileKeyColumns = ["class_title", "start_date"], // Default mobile key columns
  ...extra
}) => {
  const [sort, setSort] = useState({
    column: default_sort_column,
    direction: default_sort_direction,
  });
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortedData, setSortedData] = useState(data);
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Check if screen is mobile size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768); // Bootstrap md breakpoint
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Toggle expanded row
  const toggleExpandedRow = (rowKey) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowKey)) {
      newExpandedRows.delete(rowKey);
    } else {
      newExpandedRows.add(rowKey);
    }
    setExpandedRows(newExpandedRows);
  };

  // Change the number of rows per page
  const changeRowsPerPage = (e) => {
    setRowsPerPage(e.target.value);
  };

  // Sort the table based on the column clicked
  const sortTable = (column) => {
    if (sort.column === column) {
      setSort({
        column,
        direction: sort.direction === "asc" ? "desc" : "asc",
      });
    } else {
      setSort({ column, direction: "asc" });
    }
  };

  const sortData = (_data = []) => {
    return _data.sort((a, b) => {
      if (sort.column) {
        if (a[sort.column] > b[sort.column]) {
          return sort.direction === "asc" ? -1 : 1;
        }
        if (a[sort.column] < b[sort.column]) {
          return sort.direction === "asc" ? 1 : -1;
        }
      }
      return 0;
    });
  };

  // Sort the data any time the sort state changes
  useEffect(() => {
    let newSort = sortData(sortedData);
    // Spread the sorted data into a new array to force a re-render
    setSortedData([...newSort]);
  }, [sort]);

  // Update the sorted data any time the data changes
  // Update the number of pages any time the data changes
  useEffect(() => {
    const newSort = sortData(data);
    // Create a new array reference to ensure React detects the change
    setSortedData([...newSort]);
    let newPageCount = Math.max(
      isNaN(rowsPerPage) ? 1 : Math.ceil(data.length / rowsPerPage),
      1
    );
    setPageCount(newPageCount);
    if (currentPage > newPageCount + 1) {
      setCurrentPage(newPageCount);
    }
  }, [data, sort.column, sort.direction, rowsPerPage]);

  // Force key update when sortedData changes to ensure re-render
  const tableKey = JSON.stringify(
    sortedData.map(
      (row) => row.id || row.signupid || row._id || row.key || Math.random()
    )
  );

  // Update the number of pages any time the rows per page changes
  useEffect(() => {
    let newPageCount = Math.max(
      isNaN(rowsPerPage) ? 1 : Math.ceil(data.length / rowsPerPage),
      1
    );
    setPageCount(newPageCount);
    if (currentPage > newPageCount + 1) {
      setCurrentPage(newPageCount);
    }
  }, [rowsPerPage]);

  const pages =
    pageCount <= 1
      ? [1]
      : pageCount === 2
      ? [1, 2]
      : currentPage === 1
      ? [currentPage, currentPage + 1, currentPage + 2]
      : currentPage === pageCount
      ? [currentPage - 2, currentPage - 1, currentPage]
      : [currentPage - 1, currentPage, currentPage + 1];

  // Mobile Card Component
  const MobileCard = ({ row, index }) => {
    const rowKey =
      row.id || row.signupid || row._id || row.key || `row-${index}`;
    const isExpanded = expandedRows.has(rowKey);
    const rowIsClickable = onRowClick !== null && isRowClickable(row);

    // Get key columns for mobile display
    const keyHeaders = headers.filter((header) =>
      mobileKeyColumns.includes(header.key)
    );
    const otherHeaders = headers.filter(
      (header) => !mobileKeyColumns.includes(header.key)
    );

    const handleCardClick = (e) => {
      // Don't expand if clicking on action buttons
      if (e.target.closest("button")) {
        return;
      }

      // In mobile view, card click only toggles expand/collapse
      toggleExpandedRow(rowKey);
    };

    const handleExpandClick = (e) => {
      e.stopPropagation();
      toggleExpandedRow(rowKey);
    };

    const handleEditClick = (e) => {
      e.stopPropagation();
      if (rowIsClickable && onRowClick) {
        onRowClick(row);
      }
    };

    return (
      <div
        className="card mb-2 mobile-card-clickable"
        onClick={handleCardClick}
      >
        <div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              {keyHeaders.map(({ key, text, render }) => (
                <div key={key} className="mb-1">
                  <strong className="text-muted small">{text}:</strong>
                  <div className="fw-bold">
                    {row[key] instanceof Date
                      ? formatCentralTime(row[key])
                      : render
                      ? render(row, extra)
                      : row[key]}
                  </div>
                </div>
              ))}
            </div>
            <div className="d-flex align-items-start">
              {rowIsClickable && (
                <button
                  className="btn btn-primary btn-sm me-2"
                  onClick={handleEditClick}
                  aria-label="Edit"
                >
                  <i className="fa-solid fa-pen-to-square"></i>
                </button>
              )}
              <button
                className="btn btn-link btn-sm p-1"
                onClick={handleExpandClick}
                aria-label={isExpanded ? "Collapse details" : "Expand details"}
              >
                <i
                  className={`fa-solid ${
                    isExpanded ? "fa-chevron-up" : "fa-chevron-down"
                  }`}
                ></i>
              </button>
            </div>
          </div>

          {isExpanded && (
            <div className="mt-3 pt-3 border-top">
              {otherHeaders.map(({ key, text, render }) => (
                <div key={key} className="row mb-2">
                  <div className="col-5">
                    <strong className="text-muted small">{text}:</strong>
                  </div>
                  <div className="col-7">
                    {row[key] instanceof Date
                      ? formatCentralTime(row[key])
                      : render
                      ? render(row, extra)
                      : row[key]}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="sortable-table-controls mb-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
          {/* Left side: Controls */}
          <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-3 flex-wrap">
            <div className="d-flex align-items-center gap-2">
              <label className="form-label mb-0 text-nowrap">
                Rows per page:
              </label>
              <select
                id="rowsPerPage"
                className="form-select form-select-sm"
                value={rowsPerPage}
                onChange={changeRowsPerPage}
                style={{ width: "80px" }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="all">All</option>
              </select>
            </div>

            <div className="d-flex align-items-center">
              <small className="text-muted">Pages: {pageCount}</small>
            </div>

            {extra_controls && (
              <div className="d-flex flex-wrap align-items-center gap-3">
                {extra_controls}
              </div>
            )}
          </div>

          {/* Right side: Pagination */}
          <nav className="mt-2 mt-md-0">
            <ul className="pagination pagination-sm mb-0">
              <li className="page-item">
                <div
                  className={`page-link ${currentPage <= 1 ? "disabled" : ""}`}
                  onClick={() =>
                    currentPage > 1 && setCurrentPage(currentPage - 1)
                  }
                  style={{
                    cursor: currentPage <= 1 ? "not-allowed" : "pointer",
                  }}
                >
                  Previous
                </div>
              </li>
              <li className="page-item">
                <div
                  className={`page-link ${
                    currentPage === pages[0] ? "active" : ""
                  }`}
                  onClick={() => setCurrentPage(pages[0])}
                  style={{ cursor: "pointer" }}
                >
                  {pages[0]}
                </div>
              </li>
              {pageCount > 1 ? (
                <li className="page-item">
                  <div
                    className={`page-link ${
                      currentPage === pages[1] ? "active" : ""
                    }`}
                    onClick={() => setCurrentPage(pages[1])}
                    style={{ cursor: "pointer" }}
                  >
                    {pages[1]}
                  </div>
                </li>
              ) : null}
              {pageCount > 2 ? (
                <li className="page-item">
                  <div
                    className={`page-link ${
                      currentPage === pages[2] ? "active" : ""
                    }`}
                    onClick={() => setCurrentPage(pages[2])}
                    style={{ cursor: "pointer" }}
                  >
                    {pages[2]}
                  </div>
                </li>
              ) : null}
              <li className="page-item">
                <div
                  className={`page-link ${
                    currentPage >= pageCount ? "disabled" : ""
                  }`}
                  onClick={() =>
                    currentPage < pageCount && setCurrentPage(currentPage + 1)
                  }
                  style={{
                    cursor:
                      currentPage >= pageCount ? "not-allowed" : "pointer",
                  }}
                >
                  Next
                </div>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Mobile View */}
      {isMobile ? (
        <div className="mobile-table-view">
          {sortedData
            .slice(
              (currentPage - 1) *
                (isNaN(rowsPerPage) ? data.length : rowsPerPage),
              currentPage * (isNaN(rowsPerPage) ? data.length : rowsPerPage)
            )
            .map((row, index) => (
              <MobileCard
                key={
                  row.id || row.signupid || row._id || row.key || `row-${index}`
                }
                row={row}
                index={index}
              />
            ))}
        </div>
      ) : (
        /* Desktop Table View */
        <table className="table table-striped table-hover table-bordered">
          <thead>
            <tr>
              {headers.map(({ key, text }) => {
                return (
                  <td
                    key={key}
                    className={[
                      "clickable",
                      key === sort.column ? "sortable" : "",
                      key === sort.column && sort.direction === "desc"
                        ? ""
                        : "desc",
                    ].join(" ")}
                    onClick={() => sortTable(key)}
                  >
                    {text}
                  </td>
                );
              })}
            </tr>
          </thead>
          <tbody key={tableKey}>
            {sortedData
              .slice(
                (currentPage - 1) *
                  (isNaN(rowsPerPage) ? data.length : rowsPerPage),
                currentPage * (isNaN(rowsPerPage) ? data.length : rowsPerPage)
              )
              .map((row, index) => {
                const rowIsClickable =
                  onRowClick !== null && isRowClickable(row);
                return (
                  <tr
                    key={
                      row.id ||
                      row.signupid ||
                      row._id ||
                      row.key ||
                      `row-${index}`
                    }
                    className={rowIsClickable ? "clickable" : ""}
                    onClick={() => (rowIsClickable ? onRowClick(row) : null)}
                  >
                    {headers.map(({ key }) => (
                      <td key={key}>
                        {row[key] instanceof Date
                          ? // Format date in Central Time
                            formatCentralTime(row[key])
                          : // If the headers[key].render is a function, call it to render the data
                          headers.find((header) => header.key === key).render
                          ? headers
                              .find((header) => header.key === key)
                              .render(row, extra)
                          : row[key]}
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
      )}
    </>
  );
};

export default SortableTable;
